import { useState, useEffect } from "react";
import { Plus, Trash2, Shield, Info, CheckCircle } from "react-feather";
import { Modal } from "../../../../components/Modal";
import { Colors } from "../../../../values/colors";
import RiskSurveyService from "../../../../services/RiskSurvey";
import ActionPlansService from "../../../../services/ActionPlans";
import {
  ModalTitle,
  TitleIcon,
  FieldLabel,
  TextInput,
  TextArea,
  SelectInput,
  FieldGroup,
  TwoCol,
  FooterRow,
  CancelBtn,
  ConfirmBtn,
  ConfirmBody,
  ConfirmIcon,
  ConfirmText,
  SeparationColumn,
  SessionMarkers,
  SectionDot,
  PlanNameQuote,
  DeclarationBox,
  DeclarationHeader,
  DeclarationText,
  AuditNote,
} from "./styles";

const URGENCY_OPTIONS = [
  { value: "critical", label: "Crítica" },
  { value: "high", label: "Alta" },
  { value: "medium", label: "Média" },
];

const LIFECYCLE_OPTIONS = [
  { value: "ongoing", label: "Em Andamento" },
  { value: "overdue", label: "Atrasada" },
  { value: "finalized", label: "Concluída" },
];

const makeTitle = (text) => (
  <ModalTitle>
    <TitleIcon><Plus size={16} /></TitleIcon>
    {text}
  </ModalTitle>
);

const makePracticedTitle = (text) => (
  <ModalTitle>
    <TitleIcon><Shield size={16} /></TitleIcon>
    {text}
    <Info size={14} color="#A8BAC8" style={{ marginLeft: 2 }} />
  </ModalTitle>
);

const CreateEditContent = ({ plan, onClose, onSave }) => {
  const [title, setTitle] = useState(plan?.title ?? "");
  const [description, setDescription] = useState(plan?.description ?? "");
  const [urgency, setUrgency] = useState(plan?.priority ?? "medium");
  const [lifecycle, setLifecycle] = useState(plan?.status ?? "ongoing");
  const [actionType, setActionType] = useState(plan?.action_type ?? "");
  const [categoryId, setCategoryId] = useState(plan?.action_category_id ?? "");
  const [surveyId, setSurveyId] = useState(plan?.survey_id ?? "");
  const [dueDate, setDueDate] = useState(
    plan?.due_date ? plan.due_date.toString().split("T")[0] : ""
  );

  const [surveys, setSurveys] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          RiskSurveyService.list(),
          ActionPlansService.listCategories(),
        ]);
        setSurveys(s.surveys || []);
        setCategories(c || []);
      } catch {
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDateValid = dueDate && new Date(dueDate) >= today;

  const canSubmit =
    title.trim() &&
    description.trim() &&
    actionType.trim() &&
    categoryId &&
    surveyId &&
    dueDateValid;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSave({
      ...(plan || {}),
      title,
      description,
      status: lifecycle,
      priority: urgency,
      action_type: actionType,
      action_category_id: parseInt(categoryId),
      survey_id: parseInt(surveyId),
      due_date: dueDate,
    });
    onClose();
  };

  return (
    <>
      <SeparationColumn>
        <div>
          <SessionMarkers>
            <SectionDot />
            Identifcação
          </SessionMarkers>
          <FieldGroup>
            <FieldLabel>Título da ação</FieldLabel>
            <TextInput
              placeholder="Ex: Treinamento de Gestão de Tempo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={155}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Descrição</FieldLabel>
            <TextArea
              placeholder="Descreva o objetivo e detalhes desta ação..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={255}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Pesquisa vinculada</FieldLabel>
            <SelectInput
              value={surveyId}
              onChange={(e) => setSurveyId(e.target.value)}
              disabled={loadingMeta}
              required
            >
              <option value="">Selecione...</option>
              {surveys.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </SelectInput>
          </FieldGroup>
        </div>

        <div>
          <SessionMarkers>
            <SectionDot />
            Classificação
          </SessionMarkers>
          <FieldGroup>
            <FieldLabel>Tipo de ação</FieldLabel>
            <TextInput
              placeholder="Ex: Treinamento, Workshop, Palestra..."
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Categoria da Ação</FieldLabel>
            <SelectInput
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={loadingMeta}
              required
            >
              <option value="">Selecione...</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </SelectInput>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Prioridade</FieldLabel>
            <SelectInput value={urgency} onChange={(e) => setUrgency(e.target.value)} required>
              {URGENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </SelectInput>
          </FieldGroup>
          
          <SessionMarkers>
            <SectionDot />
            Execução
          </SessionMarkers>

          <TwoCol>
            <FieldGroup>
              <FieldLabel>Status</FieldLabel>
              <SelectInput value={lifecycle} onChange={(e) => setLifecycle(e.target.value)} required>
                {LIFECYCLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </SelectInput>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>Data limite</FieldLabel>
              <TextInput
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                required
              />
            </FieldGroup>
          </TwoCol>


          <FooterRow>
            <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
            <ConfirmBtn bg={Colors.primary} disabled={!canSubmit} onClick={handleSubmit}>
              {plan ? "Salvar" : "Criar Ação"}
            </ConfirmBtn>
          </FooterRow>
        </div>
      </SeparationColumn>
    </>
  );
};

const ConfirmPracticedContent = ({ plan, onClose, onConfirm }) => (
  <>
    <ConfirmBody>
      <PlanNameQuote>"{plan?.title}"</PlanNameQuote>
      <DeclarationBox>
        <DeclarationHeader>
          <CheckCircle size={14} /> Declaração de comprometimento
        </DeclarationHeader>
        <DeclarationText>
          Ao marcar esta ação como praticada, você declara que a medida foi
          efetivamente implementada na organização. Este registro integra o
          histórico de acompanhamento e demonstra o comprometimento da empresa
          com a saúde psicossocial dos colaboradores, conforme exigido pela NR-01.
        </DeclarationText>
      </DeclarationBox>
      <AuditNote>
        Esta informação poderá ser utilizada em auditorias e comprovações de
        conformidade regulatória.
      </AuditNote>
    </ConfirmBody>
    <FooterRow>
      <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
      <ConfirmBtn bg={Colors.secondaryGreen} onClick={onConfirm}>
        <CheckCircle size={14} /> Confirmar como praticada
      </ConfirmBtn>
    </FooterRow>
  </>
);

const ConfirmDeleteContent = ({ onClose, onConfirm }) => (
  <>
    <ConfirmBody>
      <ConfirmIcon color={Colors.red}>
        <Trash2 size={22} /> Deletar Ação?
      </ConfirmIcon>
      <ConfirmText>Você tem certeza que deseja deletar esta ação? Esta operação não pode ser desfeita.</ConfirmText>
    </ConfirmBody>
    <FooterRow>
      <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
      <ConfirmBtn bg={Colors.red} onClick={onConfirm}>
        <Trash2 size={14} /> Deletar
      </ConfirmBtn>
    </FooterRow>
  </>
);

const MODAL_CONFIG = {
  create: { title: makeTitle("Nova Ação") },
  edit: { title: makeTitle("Editar Ação") },
  confirmDelete: { title: null },
  confirmPracticed: { title: makePracticedTitle("Confirmar Ação") },
};

export const ActionPlanModal = ({
  open,
  mode,
  plan,
  onClose,
  onCreate,
  onSave,
  onDelete,
  onConfirmPracticed,
}) => {
  const config = MODAL_CONFIG[mode] ?? {};

  const renderContent = () => {
    switch (mode) {
      case "create":
        return (
          <CreateEditContent
            plan={null}
            onClose={onClose}
            onSave={onCreate}
          />
        );
      case "edit":
        return (
          <CreateEditContent
            plan={plan}
            onClose={onClose}
            onSave={onSave}
          />
        );
      case "confirmDelete":
        return (
          <ConfirmDeleteContent
            onClose={onClose}
            onConfirm={() => { onDelete(plan); onClose(); }}
          />
        );
      case "confirmPracticed":
        return (
          <ConfirmPracticedContent
            plan={plan}
            onClose={onClose}
            onConfirm={() => { onConfirmPracticed(); onClose(); }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      title={config.title}
      onClose={onClose}
      disableHeader={!config.title}
    >
      {renderContent()}
    </Modal>
  );
};
